import { TRIM_TOOL } from '@constants/trim-tool';
import {
  IVAccordionDescriptionType,
  IVAccordionItemItemProps,
} from '@modules/IVAccordion/types';

type dataObject = Record<string, any>;

const parseFAQDescription = (description: IVAccordionDescriptionType[]) => {
  let answer = '';
  description.forEach((item, idx) => {
    if (idx > 0) {
      answer += `<br />`;
    }
    answer += `<p>${item.answer}</p>`;
  });
  return answer;
};

const generateFAQDataObject = (
  faqs: IVAccordionItemItemProps[],
): Record<string, any> => {
  return faqs.map(e => {
    return {
      Question: e.heading,
      Answer: parseFAQDescription(e.description),
    };
  });
};

/**
 * Site-name declaration for Google. Emitted as its own node on every page and
 * never mutated — pageSchema is rewritten into a WebPage named after the page
 * title, so it cannot carry the site name.
 */
const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://invideo.io/#website',
  url: 'https://invideo.io/',
  name: 'invideo',
  alternateName: ['invideo AI'],
} as const;

/**
 * Generate JSON+LD Schema
 * @param {object} data - JSON LD Data
 * @param {string} url - URL of the Page
 * @param {string} lang - Language
 * @param {('WebPage'| 'FAQPage')} type - Schema Type
 */
function generateJSONLDSchema(data: dataObject, url: string, type = 'WebPage') {
  const JSONSchema = [];

  if (typeof url !== 'string') {
    throw Error(`Type of url is expected 'string' but recieved ${typeof url}`);
  }

  if (typeof data !== 'object') {
    throw Error(
      `Type of data is expected 'object' but recieved ${typeof data}`,
    );
  }

  const pageSchema: dataObject = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://invideo.io',
    name: 'invideo',
  };

  const ratingSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'invideo',
    description:
      'Stop spending thousands on video creation. Boost your video marketing with our easy-to-use, time-saving and cost-effective video templates.',
    image:
      'https://web-assets.invideo.io/common/prod/logos/green_logo_with_text.svg',
    operatingSystem: 'Windows, MacOS, Chrome OS, Linux',
    applicationCategory: 'BrowserApplication',
    applicationSubCategory: 'Video editor',
  };

  if (type === 'WebPage') {
    pageSchema['@type'] = 'WebPage';
    pageSchema.url = url;

    pageSchema.publisher = {
      logo: {
        width: '190 px',
        height: '58 px',
        name: 'invideo Logo',
        url: 'https://web-assets.invideo.io/common/prod/logos/green_logo_with_text.svg',
        '@type': 'ImageObject',
      },
      name: 'invideo',
      url: 'https://invideo.io',
      sameAs: [
        'https://www.facebook.com/invideo.io/',
        'https://x.com/invideoofficial',
        'https://www.instagram.com/invideo.io/',
        'https://www.linkedin.com/company/invideoio',
        'https://github.com/invideoio',
      ],
      '@type': 'Organization',
    };

    if (data.Description && data.Title) {
      pageSchema.name = data.Title;
      pageSchema.description = data.Description;
    }
  }
  JSONSchema.push(pageSchema);
  JSONSchema.push(websiteSchema);
  JSONSchema.push(ratingSchema);

  if (data.faqItems) {
    JSONSchema.push(
      generateJSONLDSchemaFAQ(generateFAQDataObject(data.faqItems)),
    );
  }

  return JSONSchema;
}

/**
 * Generate FAQ Schema
 * @param {object} data - FAQ data
 * @returns {object} JSON+LD schema
 */
function generateJSONLDSchemaFAQ(data: dataObject) {
  const JSONSchema: Array<dataObject> = [];

  const faqSchema: dataObject = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
  };

  if (data && Array.isArray(data)) {
    data.forEach(d => {
      const entity = {
        '@type': 'Question',
        name: d.Question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: d.Answer,
        },
      };
      JSONSchema.push(entity);
    });

    faqSchema['mainEntity'] = JSONSchema;
  } else {
    return null;
  }

  return faqSchema;
}

/**
 * Generate JSON+LD Article Schema
 * @param {object} data - JSON LD Data
 * @param {string} url - URL of the Page
 * @param {string} lang - Language
 */
function generateJSONLDSchemaArticle(
  data: dataObject,
  url: string,
  lang = 'en',
) {
  const JSONSchema = [];

  if (typeof url !== 'string') {
    throw Error(`Type of url is expected 'string' but recieved ${typeof url}`);
  }

  if (typeof data !== 'object') {
    throw Error(
      `Type of data is expected 'object' but recieved ${typeof data}`,
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org/',
    '@type': 'BlogPosting',
    headline: data.SEO?.Title,
    articleBody: data.SEO?.Description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    url: url,
    ...(data.FeatureImage?.url && {
      image: {
        '@type': 'ImageObject',
        url: data.FeatureImage?.url,
      },
    }),
    inLanguage: lang,
    author: {
      '@type': 'Person',
      name: data.author?.Name || 'invideo Team',
    },
    publisher: {
      '@type': 'Organization',
      logo: {
        width: '190 px',
        height: '58 px',
        name: 'invideo Logo',
        url: 'https://web-assets.invideo.io/common/prod/logos/green_logo_with_text.svg',
        '@type': 'ImageObject',
      },
      name: 'invideo',
      url: 'https://invideo.io',
      sameAs: [
        'https://www.facebook.com/invideo.io/',
        'https://x.com/invideoofficial',
        'https://www.instagram.com/invideo.io/',
        'https://www.linkedin.com/company/invideoio',
        'https://github.com/invideoio',
      ],
    },
    datePublished: data.published_at,
  };
  if (data.VideoSchema.length) {
    const embedList: Array<dataObject> = [];
    data.VideoSchema.forEach((d: dataObject) => {
      embedList.push({
        '@type': 'VideoObject',
        ...(d.Name && { name: d.Name }),
        ...(d.Description && { description: d.Description }),
        ...(d.Thumbnail?.url && { thumbnailUrl: d.Thumbnail.url }),
        ...(d.UploadDate && { uploadDate: d.UploadDate }),
        ...(d.EmbedUrl && { embedUrl: d.EmbedUrl }),
      });
    });
    articleSchema.video = embedList;
  }
  JSONSchema.push(articleSchema);

  return JSONSchema;
}

function generateJSONLDSchemaHowTo(
  schemaData: Array<dataObject>,
  url: string,
  lang = 'en',
) {
  const JSONSchema: Array<dataObject> = [];

  if (typeof url !== 'string') {
    throw Error(`Type of url is expected 'string' but recieved ${typeof url}`);
  }

  if (typeof schemaData !== 'object') {
    throw Error(
      `Type of data is expected 'object' but recieved ${typeof schemaData}`,
    );
  }

  schemaData.forEach(data => {
    const howToSchema = {
      '@context': 'https://schema.org/',
      '@type': 'HowTo',
      ...(data.Title && { name: data.Title }),
      ...(data.description && { description: data.description }),
      ...(data.Image?.url && {
        image: {
          '@type': 'ImageObject',
          url: data.Image.url,
        },
      }),
      step: [],
    };

    if (data.Step && Array.isArray(data.Step)) {
      data.Step.forEach(d => {
        const entity = {
          '@type': 'HowToStep',
          ...(d.StepDescription && {
            itemListElement: {
              '@type': 'HowToDirection',
              text: d.StepDescription,
            },
          }),
          ...(d.StepImage?.url && {
            image: {
              '@type': 'ImageObject',
              url: d.StepImage.url,
            },
          }),
          ...(d.StepTitle && { name: d.StepTitle }),
          ...(d.StepUrl && { url: d.StepUrl }),
        };
        howToSchema.step.push(entity);
      });
    }

    JSONSchema.push(howToSchema);
  });

  return JSONSchema;
}

export {
  generateJSONLDSchema,
  generateJSONLDSchemaFAQ,
  generateJSONLDSchemaArticle,
  generateJSONLDSchemaHowTo,
};
